import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const AmenitiesSection = () => {
  const amenities = [
  {
    id: 1,
    icon: 'Shield',
    title: 'Seguridad 24/7',
    description: 'Vigilancia permanente, cámaras HD y control de acceso biométrico',
    color: 'primary'
  },
  {
    id: 2,
    icon: 'Trees',
    title: 'Áreas Verdes Premium',
    description: 'Más de 5,000m² de parques, jardines y zonas de recreación',
    color: 'success'
  },
  {
    id: 3,
    icon: 'Waves',
    title: 'Piscina Infinity',
    description: 'Piscina semi-olímpica climatizada con jacuzzi y zona infantil',
    color: 'primary'
  },
  {
    id: 4,
    icon: 'Dumbbell',
    title: 'Gimnasio Equipado',
    description: 'Equipamiento profesional Technogym y área de yoga/pilates',
    color: 'success'
  },
  {
    id: 5,
    icon: 'Users',
    title: 'Salón de Eventos',
    description: 'Capacidad 100 personas con cocina, terraza y equipamiento completo',
    color: 'primary'
  },
  {
    id: 6,
    icon: 'Car',
    title: 'Estacionamiento VIP',
    description: 'Estacionamiento techado con carga eléctrica para vehículos',
    color: 'success'
  },
  {
    id: 7,
    icon: 'Zap',
    title: 'Energía Solar',
    description: 'Sistema fotovoltaico que reduce costos en áreas comunes',
    color: 'primary'
  },
  {
    id: 8,
    icon: 'Wifi',
    title: 'Fibra Óptica 1Gbps',
    description: 'Internet de alta velocidad disponible en todo el residencial',
    color: 'success'
  }];


  const images = [
  {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_172db6329-1767887198999.png",
    alt: 'Vista aérea de residencial moderno con áreas verdes extensas, piscina comunitaria azul brillante, senderos peatonales y casas contemporáneas con techos rojos'
  },
  {
    src: "https://images.unsplash.com/photo-1721614463238-c327d3d42ccf",
    alt: 'Interior de casa moderna con sala de estar amplia, ventanales grandes, muebles contemporáneos en tonos neutros y decoración minimalista elegante'
  },
  {
    src: "https://images.unsplash.com/photo-1688569460437-af3cff8bed61",
    alt: 'Piscina comunitaria con agua cristalina, tumbonas blancas alrededor, palmeras decorativas y área de recreación con sombrillas en día soleado'
  }];


  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section id="amenities" className="py-20 md:py-28 lg:py-36 bg-gradient-to-b from-background via-muted/20 to-background relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-20 right-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-[500px] h-[500px] bg-success/5 rounded-full blur-3xl" />
      </div>
      <div className="relative z-10 px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}>

          <motion.div
            className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-success mb-6 shadow-xl"
            animate={{ rotate: [0, 5, -5, 0] }}
            transition={{ duration: 4, repeat: Infinity }}>

            <Icon name="Sparkles" size={36} className="text-primary-foreground" />
          </motion.div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-black text-foreground mb-6">
            Amenidades <span className="bg-gradient-to-r from-primary to-success bg-clip-text text-transparent">Premium</span>
          </h2>
          <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">
            Instalaciones de lujo diseñadas para elevar tu calidad de vida 🌟
          </p>
        </motion.div>

        {/* Image Gallery */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}>

          {images?.map((image, index) =>
          <motion.div
            key={index}
            variants={itemVariants}
            className="relative overflow-hidden rounded-2xl aspect-[4/3] group cursor-pointer"
            whileHover={{ scale: 1.05, y: -10 }}
            transition={{ duration: 0.3 }}>

              <Image
              src={image?.src}
              alt={image?.alt}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />

              <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </motion.div>
          )}
        </motion.div>

        {/* Amenities Grid */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}>

          {amenities?.map((amenity, index) =>
          <motion.div
            key={amenity?.id}
            variants={itemVariants}
            className="group p-8 rounded-2xl bg-gradient-to-br from-card to-muted/30 border-2 border-border hover:border-primary/50 transition-all duration-300 cursor-pointer"
            whileHover={{ y: -10, scale: 1.03 }}>

              <motion.div
              className={`inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-br from-${amenity?.color}/20 to-${amenity?.color}/10 mb-5 group-hover:scale-110 transition-transform shadow-lg`}
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.6 }}>

                <Icon name={amenity?.icon} size={32} className={`text-${amenity?.color}`} />
              </motion.div>
              <h3 className="text-xl font-heading font-bold text-foreground mb-3">
                {amenity?.title}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {amenity?.description}
              </p>
            </motion.div>
          )}
        </motion.div>

        {/* CTA Section */}
        <motion.div
          className="mt-20 p-12 md:p-16 rounded-3xl bg-gradient-to-r from-primary via-success to-primary relative overflow-hidden shadow-2xl"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}>

          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
            animate={{ x: ['-100%', '100%'] }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }} />

          
          <div className="relative z-10 text-center">
            <h3 className="text-3xl md:text-4xl font-heading font-black text-primary-foreground mb-4">
              🏞️ Vive la Experiencia Premium
            </h3>
            <p className="text-primary-foreground/90 text-xl mb-8 max-w-2xl mx-auto">
              Agenda tu visita guiada y descubre por qué Residencial 3 Volcanes es la mejor inversión
            </p>
            <motion.button
              className="px-10 py-5 bg-white text-primary rounded-full font-bold text-lg shadow-2xl hover:bg-white/90 transition-all"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}>

              <Icon name="Calendar" size={24} className="inline mr-3" />
              Agendar Visita Ahora
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );

};

export default AmenitiesSection;